import { useSelector } from 'react-redux';
/* eslint-disable no-unused-vars */
import { useMemo } from 'react';
import * as S from './LeftTableIItem.styled';
import { ImageWithFallback, ParagraphWithTooltip, CopyString } from 'components';
import { subdirectory } from 'utils';
import { Palette } from 'config';
import { ArrowDown, ArrowRightForSelectedGroup } from 'assets/icons';
import { colsWidth } from 'constants';
import ReactCountryFlag from 'react-country-flag';

const Applications = ({ item, isGrouping, isOpen, TABLE_ROW_HEIGHT }) => {
  const showAppName = useSelector((state) => state.auth.show_app_name);
  const isClickable = !!item.subElements?.length;

  return (
    <S.TableBox height={TABLE_ROW_HEIGHT} width={colsWidth['apps']}>
      <S.Container>
        {isGrouping && isOpen && isClickable ? (
          <ArrowDown style={{ fill: Palette.Primary._500, flexShrink: 0 }} />
        ) : isGrouping && isClickable ? (
          <ArrowRightForSelectedGroup style={{ fill: Palette.PrimaryGray._500, flexShrink: 0 }} />
        ) : isGrouping ? (
          <ArrowRightForSelectedGroup style={{ fill: 'transparent', flexShrink: 0 }} />
        ) : null}
        {showAppName && (
          <S.AppIcon>
            <ImageWithFallback
              src={`https://cdn.appnext.com/${subdirectory(item?.imageUrl)}/${item?.imageUrl}`}
            />
          </S.AppIcon>
        )}
        <S.TextBox width={isGrouping ? '72%' : '82%'}>
          <ParagraphWithTooltip
            text={showAppName ? item.apps : `ID: ${item.pba_number ?? ''}`}
            style={{ fontSize: '14px', fontWeight: '500', color: Palette.PrimaryGray._700 }}
          />

          {showAppName && (
            <ParagraphWithTooltip
              text={item?.package ?? ''}
              style={{ fontSize: '12px', fontWeight: '400', color: Palette.PrimaryGray._700 }}
            />
          )}
          {showAppName && (
            <CopyString
              label='ID'
              text={item.pba_number.toString()}
              className='publisherReportTableNameColCopyString'
            />
          )}
        </S.TextBox>
      </S.Container>
    </S.TableBox>
  );
};

const Date = ({ item, TABLE_ROW_HEIGHT }) => {
  const [year, month, day] = item.date.split(',');

  return (
    <S.TableBox height={TABLE_ROW_HEIGHT} width={colsWidth['apps']}>
      <S.DateParagraph>{`${day}/${month}/${year}`}</S.DateParagraph>
    </S.TableBox>
  );
};

const Country = ({ item, isGrouping, isOpen, TABLE_ROW_HEIGHT }) => {
  const isClickable = !!item.subElements?.length;
  const countryCode = useMemo(() => {
    if (item.country_code === 'UK') return 'GB';
    return item.country_code;
  }, [item.country_code]);

  return (
    <S.TableBox height={TABLE_ROW_HEIGHT}>
      <S.Container>
        {isGrouping && isOpen && isClickable ? (
          <ArrowDown style={{ fill: Palette.Primary._500, flexShrink: 0 }} />
        ) : isGrouping && isClickable ? (
          <ArrowRightForSelectedGroup style={{ fill: Palette.PrimaryGray._500, flexShrink: 0 }} />
        ) : isGrouping ? (
          <ArrowRightForSelectedGroup style={{ fill: 'transparent', flexShrink: 0 }} />
        ) : null}
        <S.DateParagraph>
          <ReactCountryFlag
            countryCode={countryCode}
            svg
            style={{
              width: '20px',
              height: '15px',
              marginRight: '8px',
            }}
          />
          {`${item.country_code} - ${item.country}`}
        </S.DateParagraph>
      </S.Container>
    </S.TableBox>
  );
};

const Advertiser = ({ item, TABLE_ROW_HEIGHT }) => {
  return (
    <S.TableBox height={TABLE_ROW_HEIGHT}>
      <S.TextBox>
        <S.DateParagraph>{item.advertiser}</S.DateParagraph>
        <S.Text>{item.adveriser_id}</S.Text>
      </S.TextBox>
    </S.TableBox>
  );
};

const Placement = ({ item, isGrouping, isOpen, TABLE_ROW_HEIGHT }) => {
  const showAppName = useSelector((state) => state.auth.show_app_name);
  const placementId = item?.placement_id ?? '';
  const isClickable = !!item.subElements?.length;

  return (
    <S.TableBox height={TABLE_ROW_HEIGHT} width={colsWidth['apps']}>
      <S.Container>
        {isGrouping && isOpen && isClickable ? (
          <ArrowDown style={{ fill: Palette.Primary._500, flexShrink: 0 }} />
        ) : isGrouping && isClickable ? (
          <ArrowRightForSelectedGroup style={{ fill: Palette.PrimaryGray._500, flexShrink: 0 }} />
        ) : isGrouping ? (
          <ArrowRightForSelectedGroup style={{ fill: 'transparent', flexShrink: 0 }} />
        ) : null}
        <S.TextBox width={isGrouping ? '90%' : '100%'}>
          <ParagraphWithTooltip
            text={`${item.placement} (${showAppName ? item.apps : item.pba_number})`}
            style={{ fontSize: '14px', fontWeight: '500', color: Palette.PrimaryGray._700 }}
          />
          <S.FlexContainer>
            <ParagraphWithTooltip
              text={`ID: ${placementId}`}
              style={{ fontSize: '12px', fontWeight: '400', color: Palette.PrimaryGray._700 }}
            />
            <CopyString isHidden label='' text={placementId} />
          </S.FlexContainer>
        </S.TextBox>
      </S.Container>
    </S.TableBox>
  );
};

const SubRow = ({ item, TABLE_ROW_HEIGHT, type }) => {
  const showAppName = useSelector((state) => state.auth.show_app_name);
  let title = '';
  let countryCode = '';
  let placement_id = '';
  if (type === 'placements') {
    title = `${item.placement} - ${showAppName ? item.apps : item.pba_number}`;
    placement_id = `${item.placement_id}`;
  }
  if (type === 'country') {
    title = /[a-zA-Z]/.test(item.country_code) ? `${item.country_code} - ${item.country}` : 'N/A';
    countryCode = item.country_code === 'UK' ? 'GB' : item.country_code;
  }

  return (
    <S.TableBox height={TABLE_ROW_HEIGHT} width={colsWidth['apps']}>
      <S.Line></S.Line>
      {type === 'country' && (
        <S.SubRowWrap>
          <ReactCountryFlag
            countryCode={countryCode}
            svg
            style={{
              width: '20px',
              height: '15px',
              marginRight: '8px',
            }}
          />
          <S.SubRowText>{title}</S.SubRowText>
        </S.SubRowWrap>
      )}
      {type === 'placements' && (
        <S.PlacementWrapper>
          <S.TextWrapper>
            <ParagraphWithTooltip
              text={title}
              style={{
                fontSize: '16px',
                fontWeight: '500',
                color: Palette.PrimaryGray._700,
                marginBottom: '5px',
              }}
            />
            <ParagraphWithTooltip
              text={`ID:${placement_id}`}
              style={{ fontSize: '12px', fontWeight: '400', color: Palette.PrimaryGray._700 }}
            />
          </S.TextWrapper>
        </S.PlacementWrapper>
      )}
    </S.TableBox>
  );
};

export const LeftTableItem = {
  applications: Applications,
  date: Date,
  country: Country,
  advertiser: Advertiser,
  placement: Placement,
  sub_element: SubRow,
};
